import type {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactElement,
} from "react";
import Icon from "./Icon";
import type { IconName } from "./Icon";
import { hasValidationError, inputClasses } from "./inputClasses";
import type { ValidationIssue } from "~/kernel/validation/ValidationIssue";
import React from "react";

export type TextFieldProps = {
  id: string;
  name?: string;
  ariaLabel?: string;
  type?:
    | "email"
    | "text"
    | "password"
    | "tel"
    | "time"
    | "search"
    | "number"
    | "color";
  defaultValue?: string | null;
  required?: boolean;
  autocomplete?:
    | "off"
    | "name"
    | "email"
    | "current-password"
    | "new-password"
    | "tel";
  list?: string;
  value?: string | number;
  pattern?: string;
  placeholder?: string;
  disabled?: boolean;
  tabIndex?: number;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler;
  onClick?: MouseEventHandler;
  onIconClick?: MouseEventHandler;
  readOnly?: boolean;
  iconName?: IconName;
  iconTabIndex?: number;
  appendElement?: ReactElement;
  error?: string | ValidationIssue[];
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
  userAgentAppearance?: boolean;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onContextMenu?: MouseEventHandler<HTMLInputElement>;
  inputMode?: "numeric";
  step?: number;
};

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      id,
      type,
      defaultValue,
      required,
      autocomplete,
      value,
      pattern,
      placeholder,
      list,
      inputMode,
      disabled,
      tabIndex,
      onChange,
      onBlur,
      onClick,
      autoFocus,
      onIconClick,
      readOnly,
      iconName,
      name = id,
      appendElement,
      onKeyDown,
      error,
      userAgentAppearance = true,
      ariaLabel,
      onFocus,
      onContextMenu,
      iconTabIndex,
      step,
    }: TextFieldProps,
    ref,
  ) => {
    const getValueProps = () => {
      if (defaultValue) {
        return { defaultValue };
      }

      return { value, onChange };
    };

    return (
      <div className="relative flex flex-row">
        <input
          step={step}
          ref={ref}
          aria-label={ariaLabel}
          aria-invalid={hasValidationError(error) || undefined}
          inputMode={inputMode}
          onInput={(e) => (e.target as HTMLInputElement).setCustomValidity("")}
          onClick={onClick}
          readOnly={readOnly}
          name={name}
          {...getValueProps()}
          onChange={onChange}
          list={list}
          tabIndex={tabIndex}
          id={id}
          onFocus={onFocus}
          onBlur={onBlur}
          required={required}
          pattern={pattern}
          disabled={disabled}
          type={type}
          placeholder={placeholder}
          autoComplete={autocomplete}
          className={inputClasses({
            error,
            appendElement,
            userAgentAppearance,
          })}
          onKeyDown={onKeyDown}
          onContextMenu={onContextMenu}
          autoFocus={autoFocus}
        />
        {appendElement && <>{appendElement}</>}
        {iconName && (
          <button
            type="button"
            onClick={onIconClick}
            className="absolute top-1/2 right-3 flex h-5 -translate-y-[50%]"
            tabIndex={iconTabIndex}
          >
            <Icon
              name={iconName}
              className="block text-lg hover:text-slate-400"
            />
          </button>
        )}
      </div>
    );
  },
);

TextField.displayName = "TextField";

export default TextField;
